import { Card, Icon } from '@/components/ui';

function months(days) {
  const count = Math.round(days / 30);
  return count === 12 ? '12 months' : `${count} ${count === 1 ? 'month' : 'months'}`;
}

function daysLabel(value) {
  // Under a day reads better as "a day" than "0.4 days".
  if (value <= 1) return 'a day';
  const rounded = Math.round(value);
  return `${rounded} days`;
}

/**
 * What the platform counted, not what the company wrote.
 *
 * Kept visually apart from the branding sections (a muted panel with its own
 * source line) because these are facts from application history that the
 * company cannot edit. Until there are enough applications the rates are
 * null, and the card says so instead of showing a percentage of three people.
 */
export function TrustSignals({ trust }) {
  if (!trust) return null;
  const period = months(trust.window_days ?? 365);

  return (
    <Card className="bg-surface-muted/60">
      <div className="mb-4 flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="bg-surface text-ink-muted ring-line grid size-8 shrink-0 place-items-center rounded-full ring-1 ring-inset"
        >
          <Icon name="verification" size={16} />
        </span>
        <div className="min-w-0">
          <h2 className="text-ink text-base font-semibold tracking-tight">Trust signals</h2>
          <p className="text-ink-muted text-xs">Counted by Wonder, not written by the company.</p>
        </div>
      </div>

      {trust.enough_data ? (
        <>
          <dl className="space-y-3 text-sm">
            {trust.response_rate !== null && trust.response_rate !== undefined && (
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-muted">Response rate</dt>
                <dd className="text-ink text-lg font-semibold tabular-nums">
                  {Math.round(trust.response_rate * 100)}%
                </dd>
              </div>
            )}
            {trust.median_response_days !== null && trust.median_response_days !== undefined && (
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-muted">Typical first reply</dt>
                <dd className="text-ink text-right">
                  Usually responds within {daysLabel(trust.median_response_days)}
                </dd>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-ink-muted">Offers accepted</dt>
              <dd className="text-ink font-semibold tabular-nums">{trust.offers_accepted}</dd>
            </div>
          </dl>
          <p className="text-ink-muted border-line mt-4 border-t pt-3 text-xs">
            Based on {trust.sample_size} {trust.sample_size === 1 ? 'application' : 'applications'}{' '}
            in the last {period}.
          </p>
        </>
      ) : (
        <p className="text-ink-muted text-sm">
          Not enough applications yet to show response times.
        </p>
      )}
    </Card>
  );
}
